import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { openSamWithPrefill } from "@/components/SamHelper";
import { api } from "@/lib/api";

const SAM_AVATAR = "/sam-avatar.png";

// Throttle impressions: at most one impression per module per page-session.
// `pageSessionKey` resets whenever a new module slug is shown, so navigating
// away and back within the same module under 5 minutes won't re-fire.
const impressionFired = new Map(); // module -> last timestamp (ms)
const IMPRESSION_THROTTLE_MS = 5 * 60 * 1000;

function trackEvent(event, module) {
  try {
    if (!module) return;
    // Fire-and-forget. Errors are swallowed — tracking must never block UX.
    api.post("/sam-tips/track", { event, module }).catch(() => {});
  } catch {
    /* silent */
  }
}

/**
 * Inline "Suggested by Sam" empty-state tip card.
 * Renders nothing unless `show` is true (so callers can simply pass
 * `show={!loading && items.length === 0}`).
 *
 * Props:
 *  - show      (bool)   render guard (default true)
 *  - message   (string) one-line contextual nudge
 *  - prefill   (string) question auto-sent to Sam on click
 *  - module    (string) module slug used for analytics (chat|leads|...).
 *  - testId    (string) data-testid for the whole card (button gets testId+"-btn")
 *  - className (string) optional extra classes for outer wrapper
 */
export function SamTipCard({ show = true, enabled = true, message, prefill, module, testId = "sam-tip", className = "" }) {
  const visible = show && enabled && message && prefill;
  const firedRef = useRef(false);

  useEffect(() => {
    if (!visible || !module || firedRef.current) return;
    const now = Date.now();
    const last = impressionFired.get(module) || 0;
    if (now - last < IMPRESSION_THROTTLE_MS) return;
    impressionFired.set(module, now);
    firedRef.current = true;
    trackEvent("impression", module);
  }, [visible, module]);

  if (!visible) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={`mb-4 flex items-center gap-3 rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-50 via-white to-teal-50/60 p-3 shadow-sm dark:border-indigo-500/20 dark:from-indigo-500/5 dark:via-white/[0.02] dark:to-teal-500/5 sm:gap-4 sm:p-4 ${className}`}
      data-testid={testId}
      role="region"
      aria-label="Suggested by Sam"
    >
      <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-white ring-2 ring-indigo-500/30 dark:bg-zinc-800">
        <img src={SAM_AVATAR} alt="" className="h-full w-full object-cover" draggable={false} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
          <Sparkles className="h-3 w-3" /> Suggested by Sam
        </div>
        <div className="mt-0.5 truncate text-sm text-slate-700 dark:text-zinc-200">
          {message}
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          if (module) trackEvent("click", module);
          openSamWithPrefill(prefill);
        }}
        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 sm:px-4 sm:text-sm"
        data-testid={`${testId}-btn`}
        aria-label="Ask Sam"
      >
        Ask Sam <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
}
